import React from 'react';
import { PillIcon, CalendarIcon, AlertTriangleIcon } from 'lucide-react';
export function Story() {
  return <section className="w-full py-12 bg-white" data-id="element-197">
      <div className="max-w-3xl mx-auto px-4" data-id="element-198">
        <div className="prose prose-lg max-w-none" data-id="element-199">
          <p className="text-lg" data-id="element-200">
            Nicht das, was Sie denken. Keine illegalen Drogen. Keine einzige
            Tablette, die nicht von deutschen Ärzten verschrieben wurde. Alles
            legal, alles von der Krankenkasse bezahlt, alles im Namen der
            "modernen Medizin."
          </p>
          <div className="bg-red-50 border-l-4 border-red-500 p-4 my-6" data-id="element-201">
            <div className="flex items-center mb-2" data-id="element-202">
              <PillIcon className="h-5 w-5 text-red-600 mr-2" data-id="element-203" />
              <span className="font-bold text-red-800" data-id="element-204">
                12 Schmerztabletten täglich
              </span>
            </div>
            <p className="text-red-800" data-id="element-205">
              Jeden Morgen um 6:30 Uhr das gleiche Ritual: Ibuprofen 600mg,
              Diclofenac 75mg, Tramadol 50mg, Gabapentin 300mg - und das war nur
              der Anfang. Über den Tag verteilt schluckte ich Pillen wie andere
              Menschen Vitamine.
            </p>
          </div>
          <p data-id="element-206">
            Meine Kinder nannten mich heimlich "Tabletten-Mama." Mein Mann
            machte sich Sorgen um meine Leber. Meine Kollegen bemerkten meine
            Vergesslichkeit und Müdigkeit. Aber das Schlimmste: Trotz all dieser
            Medikamente hatte ich immer noch Schmerzen.
          </p>
          <div className="bg-green-50 border-l-4 border-green-500 p-4 my-6" data-id="element-207">
            <div className="flex items-center mb-2" data-id="element-208">
              <CalendarIcon className="h-5 w-5 text-green-600 mr-2" data-id="element-209" />
              <span className="font-bold text-green-800" data-id="element-210">
                Heute, genau 73 Tage später
              </span>
            </div>
            <p className="text-green-800" data-id="element-211">
              Nehme ich null Tabletten. Null. Keine einzige. Und mein Hausarzt
              Dr. Mueller kann es immer noch nicht glauben. Als er die neuen
              Röntgenbilder mit den alten verglich, starrte er minutenlang
              schweigend auf die Aufnahmen und sagte dann: "Frau Weber, ich kann
              mir das medizinisch nicht erklären. Was um Himmels willen haben
              Sie gemacht?"
            </p>
          </div>
          <p data-id="element-212">
            Das ist meine Geschichte. Eine Geschichte, die zeigt, wie das
            deutsche Gesundheitssystem mich in eine Medikamenten-Falle lockte –
            und wie ich den Weg fand, mich mit einer unscheinbaren Einlegesohle,
            die auf einer österreichischen Entdeckung basiert, daraus zu
            befreien.
          </p>
          <h2 className="text-2xl font-serif font-bold text-gray-800 mt-10 mb-5" data-id="element-213">
            Der Absturz: Wie aus 2 Tabletten 12 wurden
          </h2>
          <div className="mb-8" data-id="element-214">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1XwR2XtBacrtsEsfg234KC/shutterstock_324566462-800x450.jpg" alt="Verschiedene bunte Tabletten und Medikamente" className="w-full rounded-lg shadow-md" data-id="element-215" />
          </div>
          <p data-id="element-216">
            Es begann harmlos. November 2020. Brennende Schmerzen in beiden
            Füßen nach einem 12-Stunden-Tag in der Steuerkanzlei. Die Art von
            Schmerz, die sich anfühlt, als würde man auf glühenden Kohlen
            laufen. "Das ist bestimmt nur Überanstrengung," dachte ich. Ein
            typisch deutscher Gedanke: "Das geht schon wieder weg."
          </p>
          <p data-id="element-217">
            Ging es nicht. Nach zwei Wochen unerträglicher Schmerzen ging ich zu
            meinem Hausarzt Dr. Mueller. Ein netter Mann, dem ich seit 15 Jahren
            vertraue. "Plantar Fasciitis," diagnostizierte er nach einer
            fünfminütigen Untersuchung. "Sehr häufig bei Deutschen in Ihrem
            Alter. Nehmen Sie erstmal Ibuprofen 400mg, zweimal täglich."
          </p>
          <p className="font-semibold" data-id="element-218">
            Das war der erste Schritt in die Falle.
          </p>
          <h2 className="text-2xl font-serif font-bold text-gray-800 mt-10 mb-5" data-id="element-219">
            Die deutsche Medikamenten-Eskalation - Stufe für Stufe
          </h2>
          <p data-id="element-220">
            Was dann folgte, war eine perfekt orchestrierte Eskalation, die mich
            systematisch von einer gesunden Frau zu einer medikamentenabhängigen
            Patientin machte:
          </p>
          <ul className="list-disc pl-6 my-4 space-y-2" data-id="element-221">
            <li data-id="element-222">
              <strong data-id="element-223">Monat 1-3:</strong> Ibuprofen 400mg, 2x täglich. "Wenn es
              nicht besser wird, kommen Sie wieder."
            </li>
            <li data-id="element-224">
              <strong data-id="element-225">Monat 4:</strong> Ibuprofen hilft nicht mehr → Diclofenac
              75mg dazu. "Das ist stärker. Das wird helfen."
            </li>
            <li data-id="element-226">
              <strong data-id="element-227">Monat 6:</strong> Diclofenac macht Magenschmerzen →
              Pantoprazol zum Magenschutz. "Das ist völlig normal. So schützen
              wir den Magen."
            </li>
            <li data-id="element-228">
              <strong data-id="element-229">Monat 8:</strong> Schmerzen werden schlimmer → Überweisung
              zum Orthopäden. "Ein Spezialist kann Ihnen besser helfen."
            </li>
            <li data-id="element-230">
              <strong data-id="element-231">Monat 10:</strong> Orthopäde verschreibt Tramadol 50mg.
              "Das ist für Durchbruchschmerzen. Nur bei Bedarf."
            </li>
            <li data-id="element-232">
              <strong data-id="element-233">Monat 12:</strong> "Bei Bedarf" wird zu "täglich nötig".
              "Das passiert manchmal. Wir erhöhen die Dosis."
            </li>
            <li data-id="element-234">
              <strong data-id="element-235">Monat 14:</strong> Tramadol macht Schlafprobleme →
              Zolpidem zum Schlafen. "Schlechter Schlaf verstärkt Schmerzen. Das
              ist wichtig."
            </li>
            <li data-id="element-236">
              <strong data-id="element-237">Monat 18:</strong> Neue Schmerzen in Händen und Beinen →
              Überweisung zum Neurologen. "Das könnte neuropathisch sein."
            </li>
            <li data-id="element-238">
              <strong data-id="element-239">Monat 20:</strong> Neurologe verschreibt Gabapentin 300mg.
              "Das ist speziell für Nervenschmerzen."
            </li>
            <li data-id="element-240">
              <strong data-id="element-241">Monat 24:</strong> Ständige Müdigkeit und Depressionen →
              Psychiater verschreibt Amitriptylin. "Chronische Schmerzen machen
              depressiv. Das ist ganz normal."
            </li>
          </ul>
          <div className="bg-gray-100 p-5 rounded-lg my-6" data-id="element-242">
            <h3 className="font-bold text-gray-800 mb-3" data-id="element-243">
              Das Ergebnis nach 2 Jahren:
            </h3>
            <p className="font-bold text-red-700 mb-3" data-id="element-244">
              12 verschiedene Medikamente, täglich.
            </p>
            <p data-id="element-245">Kosten: €347 monatlich nur für Tabletten.</p>
            <p className="mt-2" data-id="element-246">Und die Schmerzen? Schlimmer als je zuvor.</p>
          </div>
          <h2 className="text-2xl font-serif font-bold text-gray-800 mt-10 mb-5" data-id="element-247">
            Der Teufelskreis: Gefangen im deutschen Gesundheitssystem
          </h2>
          <p data-id="element-248">
            Was mir damals nicht klar war: Ich war zur profitablen
            Dauerpatientin geworden. Jeder Spezialist sah nur sein kleines
            Puzzleteil: Der Hausarzt sah Entzündungen, der Orthopäde mechanische
            Probleme, der Neurologe Nervenschäden, der Psychiater die
            psychischen Folgen. Niemand sah mich als ganzen Menschen.
          </p>
          <div className="bg-gray-100 p-5 rounded-lg my-6" data-id="element-249">
            <h3 className="font-bold text-gray-800 mb-3" data-id="element-250">
              Die erschreckende Bilanz nach 3 Jahren:
            </h3>
            <ul className="space-y-2" data-id="element-251">
              <li data-id="element-252">💰 €12.480 ausgegeben für Medikamente</li>
              <li data-id="element-253">💰 €8.400 für Arztbesuche und Behandlungen</li>
              <li data-id="element-254">💰 €2.100 für Zusatzuntersuchungen (MRT, Röntgen, Labor)</li>
              <li data-id="element-255">💰 €1.680 für Physiotherapie (24 Sitzungen, erfolglos)</li>
              <li data-id="element-256">
                💰 €520 für maßgefertigte Einlegesohlen (die nichts brachten)
              </li>
            </ul>
            <p className="font-bold mt-3" data-id="element-257">Gesamtkosten: €25.180</p>
            <p className="font-bold text-red-700 mt-1" data-id="element-258">Erfolg: Null.</p>
            <p className="mt-3" data-id="element-259">
              Stattdessen hatte ich jetzt chronische Magenschmerzen,
              Leberfunktionsstörungen, ständige Müdigkeit, 18kg Gewichtszunahme
              und war sozial völlig isoliert. Und immer noch die gleichen
              Fußschmerzen.
            </p>
          </div>
          <h2 className="text-2xl font-serif font-bold text-gray-800 mt-10 mb-5" data-id="element-260">
            Der Wendepunkt: Als meine Tochter mich nicht mehr erkannte
          </h2>
          <p data-id="element-261">
            Der 15. Juni 2024. Lauras Abiturfeier. Der Tag, an dem mir klar
            wurde, dass ich mein Leben verloren hatte. Ich saß benommen in der
            Ecke, schluckte heimlich Tabletten und kämpfte gegen die Übelkeit,
            als mir plötzlich schwindelig wurde und ich zusammenbrach.
          </p>
          <p data-id="element-262">
            Im Krankenwagen fragte der Notarzt nach meinen Medikamenten. Als ich
            die Liste vorlas, schaute er mich erschrocken an: "Frau Weber, das
            ist eine gefährliche Kombination. Ihr Körper ist vergiftet."
          </p>
          <p data-id="element-263">
            Die Notaufnahme-Ärztin war schonungslos ehrlich: "Ihre Leberwerte
            sind alarmierend. Wenn Sie so weitermachen, haben Sie in 2 Jahren
            irreversible Organschäden. Sie sind Opfer unseres fragmentierten
            Systems geworden."
          </p>
          <div className="border-l-4 border-blue-600 pl-4 italic text-lg text-gray-700 my-6" data-id="element-264">
            Das Schlimmste waren Lauras Tränen an meinem Krankenhausbett: "Mama,
            ich will meine richtige Mama zurück."
          </div>
          <p data-id="element-265">
            In diesem Moment wusste ich: Ich muss einen Weg finden, oder ich
            verliere alles.
          </p>
        </div>
      </div>
    </section>;
}